import type { LabeledValue } from 'antd/es/select'
import type { RawValue } from 'src/components/declare'
import type { EasyRemoteSearch } from 'src/components/common/declare'
import type { EditControlProps } from '../../declare'
import { useMemo, useRef, useContext } from 'react'
import { ListTableContext, EasyRemoteSelect } from 'src/components'
import { appStore } from 'src/stores'
import { $api } from 'src/plugins'
import { getNavFieldsFromNavField, getSaveFieldFromNavField, getRulesFromNavFilters } from 'src/components/root/helper'
import { initFilter, extendOrders, initGroup, extendGroups, getSingleRule, extendRules, extendInclude } from 'src/utils/common'
import { validGUID } from 'src/utils/tools'

const PaginationSelect = (props: EditControlProps) => {
  const table = useContext(ListTableContext)

  // 分页下拉数据
  const source = useRef([])
  const getSource = (params: EasyRemoteSearch) => {
    const {
      INPUT_DATA_API: url,
      INPUT_FILTER_ORG_TYPE: OrgType,
      INPUT_FILTER_FIELD,
      INPUT_NAV_FIELD,
      Nav_Filters,
      INPUT_SORT_FIELD,
      INPUT_SORT_ORDER,
    } = props.config
    if (!url) return new Promise(resolve => { resolve(false) })

    const { OrgId } = appStore.current.headers
    const payload = initFilter({ OrgId, OrgType, PageIndex: params.current })
    // 存在多字段排序
    const Orders: { Field: string, Order: number }[] = []
    if (INPUT_SORT_FIELD) {
      const sorts = INPUT_SORT_FIELD.split(',')
      const orders = INPUT_SORT_ORDER?.split(',') ?? []
      sorts.forEach((field: string, index: number) => {
        Orders.push({ Field: field, Order: Number(orders[index] ?? 0) })
      })
      extendOrders(payload, Orders)
    }
    // 搜索过滤字段
    const filterFields: string[] = INPUT_FILTER_FIELD?.split(',') || ['NAME', 'CODE']
    const value = params.value ?? ''
    const Groups: any = initGroup(false)
    String(value) && Groups.Rules.push(...filterFields.map(field => getSingleRule(field, 9, value as RawValue)))
    extendGroups(payload, [Groups])
    // navFields 导航字段
    const navFields = getNavFieldsFromNavField(INPUT_NAV_FIELD)
    navFields.length && extendInclude(payload, navFields)
    // 配置的过滤字段
    const filters = getRulesFromNavFilters(Nav_Filters, {
      user: appStore.current.userinfo,
      record: table?.selectedRow
    })
    extendRules(payload, filters)

    return $api['app/getDataByPost'](payload, {
      url,
      fullData: true
    }).then((res: any) => {
      source.current = res?.Data ?? []
      return res
    })
  }

  const handleChange = (data: LabeledValue | undefined) => {
    const extraFields = getSaveFieldFromNavField(data?.value, source.current, props.config)
    // 表单数据
    props.onChange?.(data?.value, extraFields)
  }

  // 下拉显示字段，用于 Option 的 label 显示
  const optionLabelFields = useMemo(() => {
    return props.config.INPUT_SHOW_FIELD?.split(',') ?? ['NAME']
  }, [props.config.INPUT_SHOW_FIELD])

  // 回填显示字段，用于选中后回填回 Select 框
  const customLabelFields = useMemo(() => {
    return props.config.INPUT_LABEL_FIELD?.split(',')
  }, [props.config.INPUT_LABEL_FIELD])

  const selectedValue = useMemo(() => {
    if (props.value === undefined) return undefined
    const isValueGUID = validGUID(props.value)
    return { label: !isValueGUID ? props.value : undefined, value: props.value }
  }, [props.value])

  const { __READONLY__ } = props.config
  return (
    <EasyRemoteSelect
      value={selectedValue}
      remoteMethod={getSource}
      onChange={val => handleChange(val as LabeledValue | undefined)}
      selectProps={{ disabled: __READONLY__ }}
      fieldOption={{ labels: optionLabelFields, inputLabels: customLabelFields }}
      style={{ minWidth: 120 }}
    />
  )
}

export default PaginationSelect